// src/components/JobLineItemsEditor.tsx
//
// Jobber-style line items editor. Per-cell save-on-blur for name/description/qty/
// unit_price; row-level delete with confirm; "+ Add line item" appends a fresh
// row with focus on its name field. Totals computed reactively from the rows
// query state; parent receives a single onTotalsChange callback that fires on
// every items-change with the full {subtotal, tax_amount, discount_amount,
// total} bundle. Job-level tax_rate / discount_type / discount_value are
// controlled props — operator edits flow up through onJobUpdate so the parent
// owns the field_jobs write.
//
// Lock state (Halt 5) is wired via isLocked prop; v4 always passes false.
// When locked, the editor renders read-only — no inputs, no add button, no
// delete buttons.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatCurrency } from '@/lib/utils'
import { computeTotals, lineTotal, type DiscountType } from '@/lib/totals'
import { lineMargin, jobMargin, jobMarginFull, formatMarginPct } from '@/lib/margin'
import type { FieldJobLineItem } from '@/lib/jobInvoiceConvert'
import Combobox from '@/components/Combobox'

type Product = {
  id: string
  name: string
  description: string | null
  unit_price: number
  unit_cost: number | null
  unit: string | null
}

function useProducts(workspaceId: string | undefined) {
  return useQuery<Product[]>({
    queryKey: ['products', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return []
      const { data, error } = await supabase
        .from('products')
        .select('id, name, description, unit_price, unit_cost, unit')
        .eq('workspace_id', workspaceId)
        .eq('is_active', true)
        .order('name')
      if (error) throw error
      return (data ?? []) as Product[]
    },
    enabled: !!workspaceId,
  })
}

type TotalsPatch = {
  subtotal: number
  tax_amount: number
  discount_amount: number
  total: number
}

type Props = {
  fieldJobId: string
  currency: string
  taxRate: number
  discountType: string
  discountValue: number
  onTotalsChange: (patch: TotalsPatch) => void
  onTaxRateChange: (rate: number) => void
  onDiscountChange: (type: string, value: number) => void
  isLocked: boolean
  // Slice 2: job-level vendor cost folded into the margin display. vendorActual =
  // SUM(vendor_bills.amount) for the job, or null when no bills. hasVendorAssignments
  // distinguishes "subbed job awaiting its bill" (unknown) from "not subbed" (N/A).
  vendorActual?: number | null
  hasVendorAssignments?: boolean
  // Slice 3: job-level labor cost. SUM(field_job_labor.amount), or null when none
  // recorded. N/A-when-absent — contributes 0 and NEVER flags partial.
  laborCost?: number | null
}

export default function JobLineItemsEditor({
  fieldJobId, currency, taxRate, discountType, discountValue,
  onTotalsChange, onTaxRateChange, onDiscountChange, isLocked,
  vendorActual = null, hasVendorAssignments = false, laborCost = null,
}: Props) {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const focusNewRowIdRef = useRef<string | null>(null)
  const { data: products = [] } = useProducts(workspace?.id)

  const { data: items = [], isLoading } = useQuery<FieldJobLineItem[]>({
    queryKey: ['field-job-line-items', fieldJobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('field_job_line_items')
        .select('*')
        .eq('field_job_id', fieldJobId)
        .order('sort_index', { ascending: true })
      if (error) throw error
      return (data ?? []) as FieldJobLineItem[]
    },
    enabled: !!fieldJobId,
  })

  // Reactive totals — delegates to the canonical computeTotals helper. Two
  // behavioral changes vs the prior inline math (both accepted on 2026-05-15
  // after a pre-flight confirmed no existing field_jobs row was relying on
  // either): (1) fixed discount is clamped to subtotal preventing negative
  // totals; (2) all returned values are cent-rounded so floating-point
  // residue never leaks into persisted DECIMAL columns.
  //
  // Field name shape (snake_case for the persist patch) is preserved so the
  // onTotalsChange callback contract upstream doesn't shift.
  const computed = useMemo(() => {
    const t = computeTotals({
      lineItems: items,
      discountType: (discountType as DiscountType) || 'none',
      discountValue,
      taxRate,
      // Arc B Slice 1: the job's minimum_charge isn't wired as a prop into this
      // editor yet (CP2 adds the input) — no floor for now.
      minimumCharge: null,
    })
    return {
      subtotal:        t.subtotal,
      discount_amount: t.discountAmount,
      tax_amount:      t.taxAmount,
      total:           t.total,
    }
  }, [items, discountType, discountValue, taxRate])

  // Computed-on-read internal margin (never persisted, staff-only). Null-cost
  // lines are excluded from the rollup (not treated as 0) — see lib/margin.ts.
  const margin = useMemo(() => jobMargin(items.map(i => ({
    unit_price: Number(i.unit_price) || 0,
    qty: Number(i.qty) || 0,
    unit_cost: i.unit_cost ?? null,
  }))), [items])

  // Slice 2: line margin + job-level vendor cost. Defaults (null / false) make
  // this reduce to the line-only margin when no vendor context is passed.
  const vmargin = useMemo(() => jobMarginFull(
    items.map(i => ({ unit_price: Number(i.unit_price) || 0, qty: Number(i.qty) || 0, unit_cost: i.unit_cost ?? null })),
    vendorActual,
    laborCost,
    { hasVendorAssignments },
  ), [items, vendorActual, laborCost, hasVendorAssignments])
  // Nothing to show when there's no known-cost-line revenue AND no vendor cost AND no labor cost.
  const marginShowable = !(vmargin.revenue === 0 && vmargin.vendorActual == null && vmargin.laborCost == null)
  const partialReason = vmargin.lineCostsPartial && vmargin.vendorCostUnknown
    ? 'line + vendor costs incomplete'
    : vmargin.vendorCostUnknown
      ? 'vendor bill not received'
      : 'some line costs missing'

  // Push computed totals up so the parent can persist them on field_jobs.
  // Guarded by a JSON-string ref to avoid spurious calls on identical recomputes.
  const lastTotalsRef = useRef('')
  useEffect(() => {
    const key = JSON.stringify(computed)
    if (key === lastTotalsRef.current) return
    lastTotalsRef.current = key
    onTotalsChange(computed)
  }, [computed, onTotalsChange])

  const addRowMutation = useMutation({
    mutationFn: async () => {
      if (!workspace?.id) throw new Error('No workspace')
      const maxSort = items.reduce((m, i) => Math.max(m, i.sort_index ?? 0), -1)
      const { data, error } = await supabase
        .from('field_job_line_items')
        .insert({
          workspace_id: workspace.id,
          field_job_id: fieldJobId,
          name: '',
          qty: 1,
          unit_price: 0,
          total: 0,
          sort_index: maxSort + 1,
        })
        .select()
        .single()
      if (error) throw error
      return data as FieldJobLineItem
    },
    onSuccess: (row) => {
      focusNewRowIdRef.current = row.id
      queryClient.invalidateQueries({ queryKey: ['field-job-line-items', fieldJobId] })
    },
    onError: () => toast.error('Failed to add line item'),
  })

  // Row update. Accepts a partial-column patch so per-cell blur saves and
  // multi-column writes (e.g. picking a library entry sets name + description +
  // unit_price + unit + product_id at once) go through the same mutation —
  // single round-trip, single optimistic-update flicker, single rollback path.
  // Row-level `total` is recomputed and written whenever qty or unit_price are
  // in the patch so the stored row stays consistent without a server trigger.
  const updateRowMutation = useMutation({
    mutationFn: async ({ rowId, patch }: { rowId: string; patch: Partial<FieldJobLineItem> }) => {
      const row = items.find(i => i.id === rowId)
      const finalPatch: Record<string, any> = { ...patch }
      if (row && (patch.qty !== undefined || patch.unit_price !== undefined)) {
        const qty = patch.qty !== undefined ? Number(patch.qty) || 0 : Number(row.qty) || 0
        const unitPrice = patch.unit_price !== undefined ? Number(patch.unit_price) || 0 : Number(row.unit_price) || 0
        // Slice 2: job lines are flat (field_job_line_items has no tier columns yet),
        // so lineTotal == qty×unitPrice — adopting it is a behavioral no-op + future-proof.
        finalPatch.total = lineTotal({ qty, unit_price: unitPrice })
      }
      const { error } = await supabase
        .from('field_job_line_items')
        .update(finalPatch)
        .eq('id', rowId)
      if (error) throw error
    },
    onMutate: async ({ rowId, patch }) => {
      await queryClient.cancelQueries({ queryKey: ['field-job-line-items', fieldJobId] })
      const snapshot = queryClient.getQueryData<FieldJobLineItem[]>(['field-job-line-items', fieldJobId])
      queryClient.setQueryData<FieldJobLineItem[]>(['field-job-line-items', fieldJobId], (old) => {
        if (!old) return old
        return old.map(row => {
          if (row.id !== rowId) return row
          const next: any = { ...row, ...patch }
          if (patch.qty !== undefined || patch.unit_price !== undefined) {
            const qty = Number(next.qty) || 0
            const unitPrice = Number(next.unit_price) || 0
            next.total = lineTotal({ qty, unit_price: unitPrice }) // Slice 2: flat job line → qty×unitPrice
          }
          return next
        })
      })
      return { snapshot }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) {
        queryClient.setQueryData(['field-job-line-items', fieldJobId], ctx.snapshot)
      }
      toast.error('Failed to save')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['field-job-line-items', fieldJobId] })
    },
  })

  const deleteRowMutation = useMutation({
    mutationFn: async (rowId: string) => {
      const { error } = await supabase
        .from('field_job_line_items')
        .delete()
        .eq('id', rowId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-job-line-items', fieldJobId] })
      toast.success('Line item deleted')
    },
    onError: () => toast.error('Failed to delete'),
  })

  const handleDelete = (rowId: string) => {
    if (!window.confirm('Delete this line item?')) return
    deleteRowMutation.mutate(rowId)
  }

  return (
    <div style={{ background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 14, padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
          Line items
        </p>
        {isLocked && (
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--warning)', background: 'var(--warning-dim)', border: '1px solid var(--warning-border)', padding: '2px 8px', borderRadius: 100, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Locked
          </span>
        )}
      </div>

      {/* Header row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.6fr 0.7fr 1fr 1fr 1fr 28px', gap: 10, padding: '0 0 8px', borderBottom: '1px solid var(--border-subtle)', marginBottom: 8 }}>
        <HeaderCell>Name</HeaderCell>
        <HeaderCell>Description</HeaderCell>
        <HeaderCell align="right">Qty</HeaderCell>
        <HeaderCell align="right" internal title="Internal cost — never shown to customers or vendors">Cost</HeaderCell>
        <HeaderCell align="right">Unit price</HeaderCell>
        <HeaderCell align="right">Total</HeaderCell>
        <span />
      </div>

      {/* Rows */}
      {isLoading ? (
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '12px 0' }}>Loading…</p>
      ) : items.length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '12px 0', fontStyle: 'italic' }}>
          {isLocked ? 'No line items on this job.' : 'No line items yet. Add one below.'}
        </p>
      ) : (
        items.map(row => (
          <LineItemRow
            key={row.id}
            row={row}
            products={products}
            currency={currency}
            isLocked={isLocked}
            shouldFocusName={focusNewRowIdRef.current === row.id}
            onClearFocusRequest={() => { if (focusNewRowIdRef.current === row.id) focusNewRowIdRef.current = null }}
            onSave={(field, value) => updateRowMutation.mutate({ rowId: row.id, patch: { [field]: value } as Partial<FieldJobLineItem> })}
            onProductSelect={(product) => updateRowMutation.mutate({
              rowId: row.id,
              patch: {
                name: product.name,
                description: product.description,
                unit_price: Number(product.unit_price) || 0,
                // Pre-fill cost from the catalog. ?? null preserves a real 0 cost
                // and keeps null (unknown) as null — never defaults to 0.
                unit_cost: product.unit_cost ?? null,
                unit: product.unit ?? 'unit',
                product_id: product.id,
              },
            })}
            onDelete={() => handleDelete(row.id)}
          />
        ))
      )}

      {/* Add row */}
      {!isLocked && (
        <button
          onClick={() => addRowMutation.mutate()}
          disabled={addRowMutation.isPending}
          data-testid="job-line-item-add"
          style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--accent-dim)', border: '1px dashed var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          <Plus style={{ width: 13, height: 13 }} /> {addRowMutation.isPending ? 'Adding…' : 'Add line item'}
        </button>
      )}

      {/* Totals panel — always visible */}
      <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ width: 320, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <TotalsRow label="Subtotal" value={formatCurrency(computed.subtotal, currency)} />

          {/* Discount row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
              <span style={{ fontSize: 12, color: 'var(--ink-secondary)', minWidth: 60 }}>Discount</span>
              <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--hover-bg)', borderRadius: 7 }}>
                {(['none', 'percentage', 'fixed'] as const).map(t => (
                  <button key={t} onClick={() => !isLocked && onDiscountChange(t, t === 'none' ? 0 : discountValue)}
                    disabled={isLocked}
                    style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600, borderRadius: 5, cursor: isLocked ? 'default' : 'pointer', background: discountType === t ? 'var(--accent)' : 'transparent', color: discountType === t ? 'var(--accent-ink)' : 'var(--ink-secondary)', border: 'none', textTransform: 'capitalize' }}>
                    {t === 'none' ? '–' : t === 'percentage' ? '%' : '$'}
                  </button>
                ))}
              </div>
              {discountType !== 'none' && (
                <NumericInput
                  value={discountValue}
                  disabled={isLocked}
                  onSave={(v) => onDiscountChange(discountType, v)}
                  width={70}
                  prefix={discountType === 'fixed' ? '$' : ''}
                  suffix={discountType === 'percentage' ? '%' : ''}
                />
              )}
            </div>
            <span style={{ fontSize: 12, color: 'var(--ink-secondary)', minWidth: 90, textAlign: 'right' }}>
              −{formatCurrency(computed.discount_amount, currency)}
            </span>
          </div>

          {/* Tax row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
              <span style={{ fontSize: 12, color: 'var(--ink-secondary)', minWidth: 60 }}>Tax</span>
              <NumericInput
                value={taxRate}
                disabled={isLocked}
                onSave={(v) => onTaxRateChange(v)}
                width={70}
                suffix="%"
              />
            </div>
            <span style={{ fontSize: 12, color: 'var(--ink-secondary)', minWidth: 90, textAlign: 'right' }}>
              {formatCurrency(computed.tax_amount, currency)}
            </span>
          </div>

          {/* Total row — larger + bolder */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, paddingTop: 10, borderTop: '1px solid var(--border-subtle)' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)' }}>Total</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)' }}>{formatCurrency(computed.total, currency)}</span>
          </div>

          {/* Margin — internal, COMPUTED-ON-READ, never persisted, never shown to
              customers/vendors. Folds in job-level vendor cost (slice 2): margin =
              line revenue − (line cost + vendor actual). Em-dash when nothing is
              known; "partial" when EITHER line costs are incomplete OR a subbed
              job's vendor bill hasn't landed — so it never reads as precise. */}
          <div title="Internal gross margin: line revenue − (line cost + vendor cost). Never shown to customers or vendors."
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent-text)', fontStyle: 'italic' }}>
              {!marginShowable
                ? 'Margin (internal — no costs entered)'
                : vmargin.isPartial
                  ? `Margin (partial — ${partialReason})`
                  : 'Margin (internal)'}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-text)', fontStyle: 'italic', fontVariantNumeric: 'tabular-nums' }}>
              {!marginShowable ? '—' : `${formatCurrency(vmargin.margin, currency)} · ${formatMarginPct(vmargin.marginPct)}`}
            </span>
          </div>

          {/* Soft double-count warning (slice 2) — non-blocking, surfaces judgment.
              Line costs = materials you bought; vendor bills = what you pay the sub.
              If the sub's charge is in both, margin double-counts it. */}
          {vmargin.bothCostTypes && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 6, padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7 }}>
              <AlertTriangle style={{ width: 12, height: 12, color: 'var(--accent-text)', flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 10.5, color: 'var(--ink-secondary)', lineHeight: 1.4 }}>
                This job has both line-item costs and vendor bills — confirm the subcontractor's charge isn't counted in both.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Subcomponents ────────────────────────────────────────────────────────────

function HeaderCell({ children, align = 'left', internal = false, title }: { children: React.ReactNode; align?: 'left' | 'right'; internal?: boolean; title?: string }) {
  return (
    <span title={title} style={{ fontSize: 10, fontWeight: 700, color: internal ? 'var(--accent-text)' : 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: align, fontStyle: internal ? 'italic' : 'normal' }}>
      {children}
    </span>
  )
}

function LineItemRow({
  row, products, currency, isLocked, shouldFocusName, onClearFocusRequest,
  onSave, onProductSelect, onDelete,
}: {
  row: FieldJobLineItem
  products: Product[]
  currency: string
  isLocked: boolean
  shouldFocusName: boolean
  onClearFocusRequest: () => void
  onSave: (field: keyof FieldJobLineItem, value: any) => void
  onProductSelect: (product: Product) => void
  onDelete: () => void
}) {
  const computedRowTotal = (Number(row.qty) || 0) * (Number(row.unit_price) || 0)
  // Internal per-line margin (computed-on-read). null when unit_cost unknown.
  const lm = lineMargin(Number(row.unit_price) || 0, Number(row.qty) || 0, row.unit_cost ?? null)

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.6fr 0.7fr 1fr 1fr 1fr 28px', gap: 10, alignItems: 'center', padding: '6px 0' }}>
      <NameCell
        row={row}
        products={products}
        isLocked={isLocked}
        shouldFocusName={shouldFocusName}
        onClearFocusRequest={onClearFocusRequest}
        onFreeformSave={(v) => onSave('name', v)}
        onProductSelect={onProductSelect}
      />
      <CellTextInput
        value={row.description ?? ''}
        disabled={isLocked}
        placeholder="Description"
        onSave={(v) => onSave('description', v || null)}
      />
      <CellNumericInput
        value={Number(row.qty) || 0}
        disabled={isLocked}
        align="right"
        onSave={(v) => onSave('qty', v)}
      />
      {/* Internal cost cell. Deliberately NOT gated by isLocked — cost stays
          editable after the job is invoiced (late vendor/material cost), while
          qty/price/name freeze. Nullable: empty input writes NULL, not 0. */}
      <CostCell
        value={row.unit_cost ?? null}
        onSave={(v) => onSave('unit_cost', v)}
      />
      <CellNumericInput
        value={Number(row.unit_price) || 0}
        disabled={isLocked}
        align="right"
        onSave={(v) => onSave('unit_price', v)}
      />
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 13, color: 'var(--ink-primary)', fontVariantNumeric: 'tabular-nums' }}>
          {formatCurrency(computedRowTotal, currency)}
        </div>
        {/* Internal line margin %; em-dash when cost unknown. Staff-only. */}
        <div title="Internal line margin — never shown to customers or vendors"
          style={{ fontSize: 10, color: 'var(--accent-text)', fontStyle: 'italic', fontVariantNumeric: 'tabular-nums', marginTop: 1 }}>
          {lm ? formatMarginPct(lm.marginPct) : '—'}
        </div>
      </div>
      {isLocked ? (
        <span />
      ) : (
        <button onClick={onDelete} aria-label="Delete line item" data-testid="job-line-item-delete"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6 }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--danger)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-tertiary)')}>
          <Trash2 style={{ width: 13, height: 13 }} />
        </button>
      )}
    </div>
  )
}

// NameCell — wraps the generic Combobox with the line-items-specific behavior:
// freeform typing commits on blur (matching the other cells' save-on-blur), and
// product picks fire a multi-column update via onProductSelect. The
// justSelectedRef flag suppresses the redundant onFreeformSave that would
// otherwise fire on the blur immediately following a selection.
function NameCell({
  row, products, isLocked, shouldFocusName, onClearFocusRequest,
  onFreeformSave, onProductSelect,
}: {
  row: FieldJobLineItem
  products: Product[]
  isLocked: boolean
  shouldFocusName: boolean
  onClearFocusRequest: () => void
  onFreeformSave: (name: string) => void
  onProductSelect: (product: Product) => void
}) {
  const [local, setLocal] = useState(row.name ?? '')
  const initialRef = useRef(row.name ?? '')
  // Race guard: picking a library entry fires onProductSelect (multi-column
  // patch) and then immediately blurs the input — which would otherwise trigger
  // onFreeformSave with the just-set name and clobber the in-flight patch with
  // a stale single-column write. Flag set in onChange, consumed in onBlur.
  const justSelectedRef = useRef(false)

  useEffect(() => {
    setLocal(row.name ?? '')
    initialRef.current = row.name ?? ''
  }, [row.name])

  useEffect(() => {
    if (shouldFocusName) onClearFocusRequest()
  }, [shouldFocusName, onClearFocusRequest])

  const selectedProduct = products.find(p => p.id === row.product_id) ?? null

  return (
    <Combobox<Product>
      value={selectedProduct}
      onChange={(p) => {
        if (!p) return
        justSelectedRef.current = true
        onProductSelect(p)
      }}
      options={products}
      getKey={p => p.id}
      getLabel={p => p.name}
      renderOption={p => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, width: '100%' }}>
          <span style={{ fontWeight: 600, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
          <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(p.unit_price || 0)} / {p.unit || 'unit'}
          </span>
        </div>
      )}
      inputValue={local}
      onInputChange={setLocal}
      onBlur={() => {
        if (justSelectedRef.current) {
          justSelectedRef.current = false
          return
        }
        if (local !== initialRef.current) {
          initialRef.current = local
          onFreeformSave(local)
        }
      }}
      placeholder="Item name"
      disabled={isLocked}
      allowFreeform
      autoFocus={shouldFocusName}
      emptyMessage="No library matches — keep typing to save as freeform"
    />
  )
}

function CellTextInput({
  value, disabled, placeholder, autoFocus, onFocusHandled, onSave,
}: {
  value: string
  disabled: boolean
  placeholder: string
  autoFocus?: boolean
  onFocusHandled?: () => void
  onSave: (v: string) => void
}) {
  const [local, setLocal] = useState(value)
  const initialRef = useRef(value)
  const inputRef = useRef<HTMLInputElement>(null)

  // Keep local state in sync with parent value when parent value changes from
  // outside (e.g. successful save invalidates the query and refetches).
  useEffect(() => {
    setLocal(value)
    initialRef.current = value
  }, [value])

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
      onFocusHandled?.()
    }
  }, [autoFocus, onFocusHandled])

  if (disabled) {
    return (
      <span style={{ fontSize: 13, color: value ? 'var(--ink-primary)' : 'var(--ink-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value || placeholder}
      </span>
    )
  }

  return (
    <input
      ref={inputRef}
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => { if (local !== initialRef.current) { initialRef.current = local; onSave(local) } }}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') { setLocal(initialRef.current); (e.target as HTMLInputElement).blur() }
      }}
      placeholder={placeholder}
      style={{ background: 'transparent', border: '1px solid transparent', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: 'var(--ink-primary)', outline: 'none', width: '100%', boxSizing: 'border-box' }}
      onFocus={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
      onMouseEnter={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.background = 'var(--hover-bg)' }}
      onMouseLeave={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.background = 'transparent' }}
    />
  )
}

function CellNumericInput({
  value, disabled, align, onSave,
}: {
  value: number
  disabled: boolean
  align: 'left' | 'right'
  onSave: (v: number) => void
}) {
  const [local, setLocal] = useState(String(value))
  const initialRef = useRef(value)

  useEffect(() => {
    setLocal(String(value))
    initialRef.current = value
  }, [value])

  if (disabled) {
    return <span style={{ fontSize: 13, color: 'var(--ink-primary)', textAlign: align, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
  }

  return (
    <input
      type="number"
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => {
        const parsed = parseFloat(local) || 0
        if (parsed !== initialRef.current) { initialRef.current = parsed; onSave(parsed) }
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') { setLocal(String(initialRef.current)); (e.target as HTMLInputElement).blur() }
      }}
      style={{ background: 'transparent', border: '1px solid transparent', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: 'var(--ink-primary)', outline: 'none', width: '100%', textAlign: align, boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }}
      onFocus={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
      onMouseEnter={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.background = 'var(--hover-bg)' }}
      onMouseLeave={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.background = 'transparent' }}
    />
  )
}

// Nullable internal-cost cell. Empty input commits NULL ("cost unknown"), never
// 0 — distinct from CellNumericInput which coerces empty→0. Always editable (no
// isLocked/disabled): cost can be recorded after the job is invoiced, while
// price/qty freeze. Styled dashed + italic + muted to read as internal, not the
// customer-facing price.
function CostCell({ value, onSave }: { value: number | null; onSave: (v: number | null) => void }) {
  const [local, setLocal] = useState(value == null ? '' : String(value))
  const initialRef = useRef(value == null ? '' : String(value))

  useEffect(() => {
    const s = value == null ? '' : String(value)
    setLocal(s)
    initialRef.current = s
  }, [value])

  const commit = () => {
    const trimmed = local.trim()
    let next: number | null
    if (trimmed === '') {
      next = null
    } else {
      const parsed = parseFloat(trimmed)
      next = Number.isNaN(parsed) ? null : parsed
    }
    const normStr = next == null ? '' : String(next)
    if (normStr !== initialRef.current) {
      initialRef.current = normStr
      setLocal(normStr)
      onSave(next)
    }
  }

  return (
    <input
      type="number"
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') { setLocal(initialRef.current); (e.target as HTMLInputElement).blur() }
      }}
      placeholder="—"
      title="Internal cost — never shown to customers or vendors"
      style={{ background: 'transparent', border: '1px dashed var(--border-default)', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: 'var(--ink-secondary)', fontStyle: 'italic', outline: 'none', width: '100%', textAlign: 'right', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }}
      onFocus={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
      onMouseEnter={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.background = 'var(--hover-bg)' }}
      onMouseLeave={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.background = 'transparent' }}
    />
  )
}

function NumericInput({
  value, disabled, onSave, width, prefix, suffix,
}: {
  value: number
  disabled?: boolean
  onSave: (v: number) => void
  width: number
  prefix?: string
  suffix?: string
}) {
  const [local, setLocal] = useState(String(value))
  const initialRef = useRef(value)

  useEffect(() => {
    setLocal(String(value))
    initialRef.current = value
  }, [value])

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: disabled ? 'transparent' : 'var(--hover-bg)', border: `1px solid ${disabled ? 'transparent' : 'var(--border-default)'}`, borderRadius: 6, padding: '3px 6px', width }}>
      {prefix && <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{prefix}</span>}
      <input
        type="number"
        value={local}
        disabled={disabled}
        onChange={e => setLocal(e.target.value)}
        onBlur={() => {
          const parsed = parseFloat(local) || 0
          if (parsed !== initialRef.current) { initialRef.current = parsed; onSave(parsed) }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          if (e.key === 'Escape') { setLocal(String(initialRef.current)); (e.target as HTMLInputElement).blur() }
        }}
        style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 12, color: 'var(--ink-primary)', width: '100%', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
      />
      {suffix && <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{suffix}</span>}
    </div>
  )
}

function TotalsRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{label}</span>
      <span style={{ fontSize: 12, color: 'var(--ink-primary)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  )
}
